import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ArticleCard } from '../components/article-card';
import { useSavedArticlesQuery } from '../hooks/use-feed-query';
import type { FeedArticle } from '../types';

export function Saved() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const saved = useSavedArticlesQuery();

  // Adapt SavedArticleRecord (server's bookmark row) → FeedArticle shape so
  // ArticleCard can render it without a separate component path.
  const items = useMemo<FeedArticle[]>(() => {
    if (!saved.data) return [];
    return saved.data.map((r) => ({
      id: r.articleHash,
      title: r.title,
      summary: r.summary ?? undefined,
      imageUrl: r.imageUrl ?? null,
      sourceUrl: r.sourceUrl ?? '',
      channelId: '',
      channelName: r.channelName ?? '',
      channelLogo: r.channelLogo ?? null,
      country: null,
      language: r.language ?? 'en',
      publishedAt: r.publishedAt ?? '',
      isTranslated: !!r.isTranslated,
      translatedFrom: r.translatedFrom ?? null,
      isAiProcessed: false,
      summaryBullets: [],
      tags: [],
    }));
  }, [saved.data]);

  return (
    <div className="nw-app nw-app--two">
      <aside className="nw-side">
        <div className="nw-side__label">{t('web.saved.sidebar.label')}</div>
        <button type="button" className="nw-side__item is-active">
          <span className="nw-side__item-label">{t('web.saved.sidebar.saved')}</span>
        </button>
        <button type="button" className="nw-side__item" onClick={() => navigate('/home')}>
          <span className="nw-side__item-label">{t('web.saved.sidebar.backToFeed')}</span>
        </button>
      </aside>
      <section className="nw-feed">
        <div className="nw-feed__head">
          <div>
            <h1 className="nw-feed__title">{t('web.saved.title')}</h1>
            <p className="nw-feed__sub">{t('web.saved.subtitle')}</p>
          </div>
        </div>
        {saved.isLoading && (
          <div className="nw-feed__loading">
            <div className="nw-feed__loading-spinner" aria-hidden />
            {t('web.saved.loading')}
          </div>
        )}
        {!saved.isLoading && items.length === 0 && (
          <div className="nw-feed__empty">{t('web.saved.empty')}</div>
        )}
        {items.length > 0 && (
          <div className="nw-feed__grid">
            {items.map((a) => (
              <ArticleCard key={a.id} article={a} isSaved />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
